import React, { useMemo, useState } from 'react';
import { useUser } from '../../../context/UserContext';

function ImpersonationPage() {
  const { canImpersonate, impersonation, startImpersonation, stopImpersonation } = useUser();
  const [identifier, setIdentifier] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const activeLabel = useMemo(() => {
    if (!impersonation?.subject) return '';
    return impersonation.subject.name || impersonation.subject.email || impersonation.subject.uid;
  }, [impersonation]);

  if (!canImpersonate) {
    return (
      <div className="h-full overflow-y-auto p-6 md:p-8">
        <div className="max-w-3xl rounded-xl border border-amber-200 bg-amber-50 p-6">
          <h2 className="text-lg font-bold text-amber-900">Admin Access Required</h2>
          <p className="mt-2 text-sm text-amber-800">
            This page is only available to administrator accounts with impersonation permission.
          </p>
        </div>
      </div>
    );
  }

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');

    try {
      await startImpersonation({ identifier, ticketId, reason });
      setIdentifier('');
      setTicketId('');
      setReason('');
    } catch (submitError) {
      setError(submitError.message || 'Failed to start impersonation');
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    setError('');

    try {
      await stopImpersonation();
    } catch (stopError) {
      setError(stopError.message || 'Failed to stop impersonation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6 md:p-8">
      <div className="mx-auto w-full max-w-5xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Admin Impersonation</h1>
          <p className="mt-1 text-sm text-slate-600">
            Start a read-only customer session for support troubleshooting. Sessions are audited and auto-expire after 15 minutes.
          </p>
        </div>

        <div className={`rounded-xl border p-4 ${impersonation ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
          {impersonation ? (
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-amber-900">Impersonation Active</p>
                <p className="text-sm text-amber-800">
                  Viewing as <span className="font-semibold">{activeLabel}</span> · read-only · ticket {impersonation.ticketId}
                </p>
              </div>
              <button
                type="button"
                onClick={stop}
                disabled={busy}
                className="inline-flex items-center justify-center rounded-md bg-amber-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {busy ? 'Stopping…' : 'Stop impersonating'}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-sm font-semibold text-emerald-900">No active impersonation session</p>
              <p className="text-sm text-emerald-800">You are currently operating as yourself.</p>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-base font-bold text-slate-900">Start New Session</h2>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700">
              Customer email or Firebase UID
              <input
                required
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                placeholder="customer@example.com or firebase_uid"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Support ticket ID
              <input
                required
                maxLength={100}
                value={ticketId}
                onChange={(event) => setTicketId(event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                placeholder="TICKET-12345"
              />
            </label>
          </div>

          <label className="mt-4 block text-sm font-medium text-slate-700">
            Troubleshooting reason
            <textarea
              required
              minLength={10}
              maxLength={500}
              rows={4}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="mt-1 w-full resize-y rounded-md border border-slate-300 px-3 py-2"
              placeholder="Describe what issue you are debugging"
            />
          </label>

          {error && <p className="mt-4 text-sm font-medium text-red-700">{error}</p>}

          <div className="mt-5 flex justify-end">
            <button
              type="submit"
              disabled={busy || !!impersonation}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? 'Starting…' : 'Start read-only session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ImpersonationPage;
